import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useRef, useState } from 'react';
import {
    Box,
    ClickAwayListener,
    createStyles,
    makeStyles,
    MenuItem,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Picker, { IEmojiData, SKIN_TONE_LIGHT } from 'emoji-picker-react';
import QuillEditor, { QuillEditorRef } from 'components/UI/QuillEditor';
import { faSmile, faUser } from '@fortawesome/pro-regular-svg-icons';
import PreviewNotification from './PreviewNotification/PreviewNotification';
import clsx from 'clsx';
import MasterData from '@spike/masterdata-model';
import store, { RootState } from 'store';
import { CustomContentNotification } from '@spike/customcontentnotification-model';
import { useDispatch, useSelector } from 'react-redux';
import {
    CustomContentNotificationState,
    CustomContentNotificationStatus,
    fetchCustomContentNotification
} from '@spike/custom-content-notification-action';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import { Spinner } from 'components/UI';

export interface Props {
    notificationKey: string;
    title: string;
    subtitle: string;
    sectionSubtitle: string;
    headerIcon: IconDefinition;
    customContentNotification: CustomContentNotification;
    setCustomContentNotification: Dispatch<SetStateAction<CustomContentNotification>>;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            padding: '64px',

            [theme.breakpoints.down('md')]: {
                flexDirection: 'column',
                padding: '32px 16px'
            }
        },
        editContainer: {
            flexBasis: '50%',
            paddingRight: '55px',

            [theme.breakpoints.down('md')]: {
                marginBottom: '32px',
                paddingRight: '0'
            }
        },
        previewContainer: {
            backgroundColor: '#F8F8F8',
            flexBasis: '50%',
            fontFamily: 'Helvetica',
            padding: '50px',
            paddingBottom: '81px',

            [theme.breakpoints.down('md')]: {
                padding: '0'
            }
        },
        previewSms: {
            backgroundColor: '#FFFFFF',
            display: 'flex'
        },
        editHeader: {
            display: 'flex',
            gap: '15px',
            marginBottom: '10px'
        },
        descriptionContainer: {
            marginTop: '36px'
        },
        sectionTitle: {
            fontSize: '18px',
            lineHeight: '18px',
            fontWeight: 600,
            marginBottom: '16px'
        },
        sectionSubtitle: {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 400
        },
        textEditor: {
            marginTop: '16px'
        },
        textEditorTitle: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '27px',
            paddingLeft: '10px',
            marginBottom: '8px'
        },
        toolbarExtensionContainer: {
            background: '#FFFFFF',
            border: '1px solid #ccc',
            borderBottomLeftRadius: '10px',
            borderBottomRightRadius: '10px',
            marginTop: '-7px',
            position: 'relative',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        personalizationContainer: {
            display: 'flex',
            cursor: 'pointer'
        },
        emojis: {
            marginRight: '16px',
            cursor: 'pointer'
        },
        personalizationPicker: {
            display: 'flex'
        },
        personalizationIcon: {
            marginRight: '8px'
        },
        personalizationText: {
            fontSize: '14px',
            lineHeight: '14px',
            fontWeight: 500
        },
        contentCount: {
            color: '#7A7A7A',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '14px',
            textAlign: 'right'
        },
        resetContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px'
        },
        resetButton: {
            fontWeight: 500,
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: '100%',
            letterSpacing: '0px',
            textDecoration: 'underline'
        },
        editIconContainer: {
            backgroundColor: '#E4DDD5',
            borderRadius: '50px',
            width: '44px',
            height: '44px',
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            alignItems: 'center'
        },
        editIcon: {
            fontSize: '22px',
            fontWeight: 300
        },
        editHeaderTitle: {
            fontSize: '16px',
            lineHeight: '16px',
            fontWeight: 600
        },
        editHeaderSubTitle: {
            marginTop: '8px',
            fontSize: '14px',
            lineHeight: '19.6px',
            fontWeight: 400
        },
        popupPersonalization: {
            position: 'absolute',
            backgroundColor: '#FFFFFF',
            padding: '10px 20px',
            border: '1px solid #ccc',
            borderRadius: '10px',

            [theme.breakpoints.down('md')]: {
                bottom: '0'
            }
        },
        popupEmoji: {
            position: 'absolute',

            [theme.breakpoints.down('md')]: {
                bottom: '0'
            }
        }
    })
);

export const EditNotificationContent: FunctionComponent<Props> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const quillRef = useRef<QuillEditorRef>(null);
    const masterData: MasterData = store.getState().masterData.data;
    const personalizationOptions = masterData.notificationPlaceholders;

    const defaultContent = masterData.defaultContentNotifications.find(
        defaultContent => defaultContent.id === props.notificationKey
    )?.description;

    const [plainTextContent, setPlainTextContent] = useState<string>('');
    const [showEmojis, setShowEmojis] = useState(false);
    const [showPersonalization, setShowPersonalization] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [contentLength, setContentLength] = useState(0);

    const customContentNotificationState = useSelector<RootState, CustomContentNotificationState>(
        state => state.customContentNotifications
    );

    const getCustomContentNotification = () => {
        dispatch(fetchCustomContentNotification(apiClientWrapper, props.notificationKey));
    };

    useEffect(() => {
        getCustomContentNotification();
    }, []);

    useNonInitialEffect(() => {
        if (
            customContentNotificationState.status === CustomContentNotificationStatus.GetSuccess &&
            customContentNotificationState.customContentNotification
        ) {
            props.setCustomContentNotification(customContentNotificationState.customContentNotification);
            setResetKey(prev => prev + 1);
            setLoading(false);

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = customContentNotificationState.customContentNotification.content || '';
            const plain = tempDiv.innerText || tempDiv.textContent || '';
            setPlainTextContent(plain);
        }
    }, [customContentNotificationState.status]);

    const onEmojiClick = (_event: any, emojiObject: IEmojiData) => {
        const editor = quillRef.current?.getEditor();
        const position = quillRef.current?.getCursorPosition();

        if (!editor || position === null || position === undefined) return;

        editor.focus();
        editor.insertText(position, emojiObject.emoji);
        editor.setSelection(position + emojiObject.emoji.length, 0);
    };

    const insertCustomTag = (tag: string) => {
        const editor = quillRef.current?.getEditor();
        const position = quillRef.current?.getCursorPosition();

        if (!editor || position === null || position === undefined) return;

        editor.focus();

        const format = editor.getFormat(position);
        const textToInsert = `${tag}`;

        editor.insertText(position, textToInsert, format);
        editor.setSelection(position + textToInsert.length, 0);
    };

    const resetContent = () => {
        props.setCustomContentNotification(prev => ({
            ...prev,
            content: defaultContent as string
        }));
        setResetKey(prev => prev + 1);
    };

    return loading ? (
        <Spinner />
    ) : (
        <Box className={classes.container}>
            <Box className={classes.editContainer}>
                <Box className={classes.editHeader}>
                    <Box>
                        <Box className={classes.editIconContainer}>
                            <FontAwesomeIcon
                                className={classes.editIcon}
                                icon={props.headerIcon}
                            ></FontAwesomeIcon>
                        </Box>
                    </Box>
                    <Box>
                        <Typography className={classes.editHeaderTitle}>{props.title}</Typography>{' '}
                        <Typography className={classes.editHeaderSubTitle}>{props.subtitle}</Typography>
                    </Box>
                </Box>
                <Box className={classes.descriptionContainer}>
                    <Typography className={classes.sectionTitle}> Edit Content</Typography>{' '}
                    <Typography className={classes.sectionSubtitle}>{props.sectionSubtitle}</Typography>
                </Box>

                <Box className={classes.textEditor}>
                    <Typography className={classes.textEditorTitle}>Content</Typography>
                    <QuillEditor
                        ref={quillRef}
                        value={props.customContentNotification?.content}
                        resetKey={resetKey}
                        onChange={(value, plain, length) => {
                            props.setCustomContentNotification(prev => ({
                                ...prev,
                                content: props.notificationKey.includes('sms') ? plain : value
                            }));
                            if (plain !== plainTextContent) setPlainTextContent(plain);
                            setContentLength(length);
                        }}
                        toolbar={props.notificationKey.includes('sms') ? false : [['bold', 'italic', 'underline']]}
                        maxLength={500}
                    />
                    <Box className={classes.toolbarExtensionContainer}>
                        <Box className={classes.personalizationContainer}>
                            <FontAwesomeIcon
                                icon={faSmile}
                                onClick={() => setShowEmojis(true)}
                                size="xl"
                                className={classes.emojis}
                            />
                            {showEmojis && (
                                <ClickAwayListener onClickAway={() => setShowEmojis(false)}>
                                    <Box className={classes.popupEmoji}>
                                        <Picker
                                            onEmojiClick={onEmojiClick}
                                            skinTone={SKIN_TONE_LIGHT}
                                            native
                                        />
                                    </Box>
                                </ClickAwayListener>
                            )}
                            <Box
                                className={classes.personalizationPicker}
                                onClick={() => setShowPersonalization(true)}
                            >
                                <Typography className={classes.personalizationText}>
                                    <FontAwesomeIcon
                                        icon={faUser}
                                        size="xl"
                                        className={classes.personalizationIcon}
                                    />
                                    Personalization
                                </Typography>
                                {showPersonalization && (
                                    <ClickAwayListener onClickAway={() => setShowPersonalization(false)}>
                                        <Box className={classes.popupPersonalization}>
                                            {personalizationOptions.map(option => (
                                                <MenuItem
                                                    key={option.id}
                                                    onClick={() => insertCustomTag(option.description as string)}
                                                >
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                        </Box>
                                    </ClickAwayListener>
                                )}
                            </Box>
                        </Box>
                        <Typography className={classes.contentCount}> {contentLength} / 500</Typography>
                    </Box>
                    <Box className={classes.resetContainer}>
                        <Typography
                            className={classes.resetButton}
                            onClick={() => resetContent()}
                        >
                            Reset Content
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box
                className={clsx(classes.previewContainer, {
                    [classes.previewSms]: props.notificationKey.includes('sms') || isMobile
                })}
            >
                <PreviewNotification
                    content={props.customContentNotification?.content}
                    notificationKey={props.notificationKey}
                />
            </Box>
        </Box>
    );
};

export default EditNotificationContent;
